import { Box, Typography, FlexBox, TextInputWithButtonInset, TextArea, Button, Icon } from '@vp/swan';
import React, { forwardRef, useContext, useState, useRef, useEffect } from 'react';
import { EMOJIS } from './src-constants-general.js';
import { CAContext } from './src-contexts-CAContext.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { useCustomTracking } from './src-hooks-custom-hooks-useTracking.js';
import PrivacyCookiePolicy from './src-components-PrivacyCookiePolicy-PrivacyCookiePolicy.js';
import './src-components-ChatWindow-ChatFooter-ChatFooter.styles.scss.js';
import ButtonGroup from './src-components-AdvanceMessagingComponent-ButtonGroup-ButtonGroup.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { useMIAWUploadFile } from './src-hooks-miaw-useMIAWUploadFile.js';
import { isMobile } from './src-utils-mobile-utils.js';
import UploadAttachmentsPreview from './src-components-UploadAttachments-UploadAttachmentsPreview.js';
import UploadAttachmentButton from './src-components-UploadAttachments-UploadAttachmentButton.js';
import { useMIAWTypingIndicator } from './src-hooks-miaw-useMIAWTypingIndicator.js';
import { BottomFixed } from './node_modules-react-bottom-fixed-lib-esm-index.js';
import { getBrowserName } from './src-utils-app-utils.js';

const ChatFooter = forwardRef(({ onSendMessage, disableInput }, ref) => {
  const {
    chatState: { chatSession, enableChatbot, isPrivacyPolicyAccepted },
    setChatState
  } = useContext(CAContext);
  const {
    state: { isMIAWApiLoading, endChatData: miawEndChatData, isMIAWEndChatError },
    setState: setMiawState
  } = useContext(MiawChatContext);
  const {
    state: { startChatbotSession, showHelperButton, isSendMessageLoading, endChatData: chatbotEndChatData, stopChatbotMessage, isChatbotEndError },
    setState: setChatbotState
  } = useContext(ChatbotContext);
  const { sendTrackingEvent } = useCustomTracking();
  const { trigger: typingIndicator } = useMIAWTypingIndicator();
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeout = useRef(null);
  const TYPING_DELAY = 5e3;
  useEffect(() => {
    if (chatSession === "miawChatSession" || chatSession === "chatbotSession") {
      setChatState((prevState) => ({
        ...prevState,
        isPrivacyPolicyAccepted: true
      }));
    } else {
      setChatState((prevState) => ({
        ...prevState,
        isPrivacyPolicyAccepted: false
      }));
    }
  }, [chatSession]);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const [prevIsLoading, setPrevIsLoading] = React.useState(false);
  const [selectedFilePayload, setSelectedFilePayload] = React.useState([]);
  const { data: uploadData, loading: uploadLoading, trigger: triggerUpload } = useMIAWUploadFile();
  const wrapperRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const handleEmojiClick = (emoji) => {
    setMessage((prev) => prev + emoji);
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };
  const handleSendMessage = () => {
    if (selectedFilePayload?.length) {
      const trimmedMessage = message.trim();
      if (trimmedMessage && selectedFilePayload.length === 1) {
        triggerUpload({ payload: selectedFilePayload, message: trimmedMessage });
      } else {
        triggerUpload({ payload: selectedFilePayload, message: "" });
        if (trimmedMessage) {
          onSendMessage?.(trimmedMessage);
        }
      }
      if (trimmedMessage) {
        setMessage("");
      }
    } else if (message.trim() !== "") {
      onSendMessage?.(message);
      setMessage("");
    }
  };
  const handleChangeMessage = (e) => {
    setMessage(e.target?.value);
    if (chatSession !== "miawChatSession") return;
    if (!isTyping) {
      setIsTyping(true);
      typingIndicator("TypingStartedIndicator");
    }
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      setIsTyping(false);
      typingIndicator("TypingStoppedIndicator");
    }, TYPING_DELAY);
  };
  useEffect(() => {
    return () => {
      if (typingTimeout?.current) clearTimeout(typingTimeout.current);
    };
  }, []);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (!message) {
      textarea.style.height = "auto";
      return;
    }
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 69) + "px";
    const handleFocus = () => {
      if (typeof window !== "undefined" && window.innerWidth < 768) {
        const scrollToBottom = setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 300);
        return () => clearTimeout(scrollToBottom);
      }
    };
    textarea.addEventListener("focus", handleFocus);
    return () => {
      textarea.removeEventListener("focus", handleFocus);
    };
  }, [message]);
  useEffect(() => {
    if (isPrivacyPolicyAccepted) {
      if (chatSession === "initial" && enableChatbot) {
        startChatbotSession?.();
      }
    }
  }, [isPrivacyPolicyAccepted, chatSession, enableChatbot]);
  useEffect(() => {
    if (isMobile()) return;
    const isCurrentlyLoading = isSendMessageLoading || isMIAWApiLoading || false;
    if (prevIsLoading && !isCurrentlyLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
    setPrevIsLoading(isCurrentlyLoading);
  }, [isSendMessageLoading, isMIAWApiLoading, prevIsLoading]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return;
      }
      if (wrapperRef.current && !wrapperRef.current?.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document?.addEventListener("mousedown", handleClickOutside);
    return () => {
      document?.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    const errorFileName = [];
    if (uploadData && uploadData.length) {
      setSelectedFilePayload([]);
      if (message.trim() !== "") {
        onSendMessage?.(message);
        setMessage("");
      }
      uploadData.map((res) => {
        if (res.status == 415 && res.fileName) {
          errorFileName.push(res.fileName);
        }
      });
      if (errorFileName.length) {
        const formattedNames = errorFileName.map((name) => {
          return name.length > 10 ? name.slice(0, 10) + "\u2026" : name;
        });
        const message2 = formattedNames.length === 1 ? `${formattedNames[0]} is not a supported file type.` : `${formattedNames.join(", ")} are not a supported file type.`;
        setMiawState((prev) => ({
          ...prev,
          attachmentErrorMessage: message2,
          showAttachmentError: true
        }));
      }
    }
  }, [uploadData]);
  useEffect(() => {
    if (miawEndChatData || chatbotEndChatData || isChatbotEndError || isMIAWEndChatError) {
      setMessage("");
    }
  }, [miawEndChatData, chatbotEndChatData, isChatbotEndError, isMIAWEndChatError]);
  const handlePrivacyPolicyAccept = () => {
    setChatState((prevState) => ({
      ...prevState,
      isPrivacyPolicyAccepted: true
    }));
    sendTrackingEvent("Chatbot Clicked", "Chatbot:Privacy", "chatPrivacyAccepted");
  };
  const onHelperButtonSelect = (option) => {
    setChatbotState((prevState) => ({
      ...prevState,
      showHelperButton: false
    }));
    sendTrackingEvent("Chatbot Clicked", "Chatbot:SuggestionOptions", `botSuggestionOptionsClicked:${option}`);
  };
  const handleFileChange = (payload) => {
    if (payload && payload.length > 0) {
      setSelectedFilePayload((prev) => [...prev, ...payload]);
    }
  };
  function handleDeletePreview(fileName) {
    setSelectedFilePayload((prev) => prev.filter((file) => file.fileData.name !== fileName));
  }
  if (!isPrivacyPolicyAccepted && chatSession === "initial" && enableChatbot) {
    return /* @__PURE__ */ React.createElement(Box, { className: `chatWindowFooter`, compactMode: true, id: "chatWindowFooter" }, /* @__PURE__ */ React.createElement(PrivacyCookiePolicy, { onPrivacyCookiePolicyAccept: handlePrivacyPolicyAccept }));
  }
  const getFooterBox = () => {
    return /* @__PURE__ */ React.createElement(FlexBox, null, chatSession === "miawChatSession" && /* @__PURE__ */ React.createElement(Box, { mr: 4 }, /* @__PURE__ */ React.createElement(UploadAttachmentButton, { handleFileChange, attachmentMessage: message, disabled: disableInput })), /* @__PURE__ */ React.createElement(TextInputWithButtonInset, { compactMode: true, className: "chatInputContainer" }, /* @__PURE__ */ React.createElement(
      TextArea,
      {
        id: "chatInputBox",
        compactMode: true,
        className: "chatInput",
        placeholder: "Type here",
        value: message,
        onKeyDown: handleKeyDown,
        onChange: handleChangeMessage,
        ref: textareaRef,
        rows: 1,
        disabled: disableInput || isSendMessageLoading || isMIAWApiLoading
      }
    ), /* @__PURE__ */ React.createElement(
      Button,
      {
        disabled: disableInput || isSendMessageLoading || isMIAWApiLoading,
        onClick: () => setShowEmojiPicker((prev) => !prev),
        display: { xs: "none", sm: "flex" },
        ref: buttonRef
      },
      /* @__PURE__ */ React.createElement(Icon, { iconType: "emoji" })
    )), isSendMessageLoading || isMIAWApiLoading ? /* @__PURE__ */ React.createElement(
      Button,
      {
        skin: "tertiary",
        onClick: () => {
          stopChatbotMessage?.();
        },
        p: 4
      },
      /* @__PURE__ */ React.createElement(Icon, { alt: "image", iconType: "pause", skin: "error" })
    ) : /* @__PURE__ */ React.createElement(Button, { skin: "tertiary", onClick: handleSendMessage, p: 4, disabled: disableInput }, /* @__PURE__ */ React.createElement(Icon, { alt: "image", iconType: "send" })));
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showEmojiPicker && /* @__PURE__ */ React.createElement(Box, { className: "emoji-set", ref: wrapperRef, compactMode: true }, EMOJIS.map((emoji, idx) => /* @__PURE__ */ React.createElement(Typography, { key: idx, className: "emoji-button", onClick: () => handleEmojiClick(emoji) }, emoji))), /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "column", ref, style: { width: "100%" } }, !disableInput && showHelperButton && chatSession === "chatbotSession" && /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "column", className: "chat-helper-button", p: 5, compactMode: true }, /* @__PURE__ */ React.createElement(Typography, { className: "chat-helper-text", fontSkin: "body-small", textColor: "subtle", mb: 3 }, "Try with:"), /* @__PURE__ */ React.createElement(
    ButtonGroup,
    {
      actionList: [
        { label: "Help with an order", value: "Help with an order" },
        { label: "Help with a design project", value: "Help with a design project" },
        { label: "Help with checkout", value: "Help with checkout" }
      ],
      onSelect: onHelperButtonSelect,
      postion: "start",
      type: "buttons"
    }
  )), /* @__PURE__ */ React.createElement(Box, { className: `chatWindowFooter`, compactMode: true, id: "chatWindowFooter" }, /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "column", style: { width: "100%" }, compactMode: true }, selectedFilePayload && selectedFilePayload.length > 0 && /* @__PURE__ */ React.createElement(Box, { mb: "4" }, /* @__PURE__ */ React.createElement(UploadAttachmentsPreview, { selectedFilePayload, isLoading: uploadLoading, handleDeletePreview })), isMobile() && getBrowserName() === "Safari" ? /* @__PURE__ */ React.createElement(BottomFixed, { className: "chat-footer-ios" }, getFooterBox()) : getFooterBox()))));
});

export { ChatFooter as default };
//# sourceMappingURL=src-components-ChatWindow-ChatFooter-ChatFooter.js.map
